// ─── Collection tracking ────────────────────────────────────────────────────
// Binds DOM events (plus a load-time check and a promo-banner visibility
// observer) to YJApiClient.tracking.track().
// Owns no transport of its own — see api-client.js for the queue/batching/
// beacon logic.
//
// Delegated listeners on `document` are mandatory here: filtering re-renders
// #ProductGridContainer's innerHTML wholesale (see facets.js/
// collection-filters-form.js), which would destroy any directly-bound
// listener on a product card or filter input.
(function () {
  'use strict';

  function _api() {
    return window.YJApiClient;
  }

  // ── Context ─────────────────────────────────────────────────────────────

  function buildContext() {
    return {
      pageType: window.YJ?.pageType,
      path: window.location.pathname,
      template: window.YJ?.template,
      locale: window.YJ?.locale,
      collectionHandle: window.YJ?.collectionHandle,
      referrer: document.referrer || undefined,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };
  }

  /** Sends one event with the standard page context. Every tracker in this file goes through here. */
  function trackEvent(eventName, payload) {
    _api()?.tracking.track(eventName, payload, buildContext());
  }

  /**
   * Delegates `eventType` on `document`, guarding `handler` so a tracking bug
   * can never break the page it's observing.
   */
  function onDocumentEvent(eventType, handler) {
    document.addEventListener(eventType, (event) => {
      try {
        handler(event);
      } catch {
        // tracking must never break the page
      }
    });
  }

  /**
   * Resolves the product identity carried on a `.card-wrapper`'s data attributes.
   * Shared by the product-click and quick-view handlers — one source of truth.
   * @param {Element} el
   * @returns {{ productHandle: string, productId: string, position: number, collectionHandle?: string }|null}
   */
  function productFromCard(el) {
    const card = el.closest('.card-wrapper');
    if (!card) return null;

    return {
      productHandle: card.dataset.product,
      productId: card.dataset.productId,
      position: Number(card.dataset.position) || 0,
      collectionHandle: card.dataset.collection || undefined,
    };
  }

  /** @param {string} rawSearchParams */
  function parseAppliedFilters(rawSearchParams) {
    const params = new URLSearchParams(rawSearchParams);
    const filters = [];
    for (const [paramName, value] of params.entries()) {
      if (paramName.indexOf('filter.') === 0) {
        filters.push({ paramName, value });
      }
    }
    filters.sort((a, b) => (a.paramName + a.value).localeCompare(b.paramName + b.value));
    return { filters, sortBy: params.get('sort_by') || undefined };
  }

  // ── Collection page views ───────────────────────────────────────────────
  // Fired once at load, gated on pageType rather than collectionId's presence:
  // `collection` is also populated on /collections/x/products/y product pages.

  function currentPageNumber() {
    return Number(new URLSearchParams(window.location.search).get('page')) || 1;
  }

  function trackCollectionView() {
    if (window.YJ?.pageType !== 'collection') return;

    // /collections/all has no collection.id, so the handle identifies it.
    const collectionId = window.YJ.collectionId || window.YJ.collectionHandle;
    if (!collectionId) return;

    const { filters, sortBy } = parseAppliedFilters(window.location.search);

    trackEvent('collection.viewed', {
      collectionId: String(collectionId),
      collectionTitle: window.YJ.collectionTitle,
      productCount: window.YJ.collectionProductCount,
      pageNumber: currentPageNumber(),
      filterCount: filters.length,
      sortBy,
    });
  }

  try {
    trackCollectionView();
  } catch {
    // tracking must never break the page
  }

  // A restored bfcache page keeps its JS state and doesn't re-run this script,
  // so Back navigation would otherwise go uncounted.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    try {
      trackCollectionView();
    } catch {
      // tracking must never break the page
    }
  });

  // ── Product clicks ──────────────────────────────────────────────────────
  // Only cards inside the main collection grid count: the same card markup is
  // reused by recommendations, search and sliders, which are not collection
  // clicks. The title link has no card-product__link class (adding it would
  // restyle the title), so it is matched through .card__title.

  const GRID_SELECTOR = '[data-tracking-source="collection-grid"]';
  const CARD_LINK_SELECTOR = '.card-product__link, .card-wrapper__link--overlay, .card__title a[href]';
  const MIDDLE_MOUSE_BUTTON = 1;

  /** @param {Element} target */
  function trackGridProductClick(target) {
    const link = target.closest(CARD_LINK_SELECTOR);
    if (!link?.closest(GRID_SELECTOR)) return;

    const product = productFromCard(link);
    if (!product?.productHandle) return;

    trackEvent('collection.product_clicked', { ...product, source: 'collection-grid' });
  }

  onDocumentEvent('click', (event) => trackGridProductClick(event.target));

  // Middle-click opens a new tab without firing `click`.
  onDocumentEvent('auxclick', (event) => {
    if (event.button !== MIDDLE_MOUSE_BUTTON) return;
    trackGridProductClick(event.target);
  });

  // ── Quick view opens ────────────────────────────────────────────────────

  onDocumentEvent('click', (event) => {
    const trigger = event.target.closest('modal-opener[data-modal^="#QuickAdd-"] button');
    if (!trigger?.closest(GRID_SELECTOR)) return;

    const product = productFromCard(trigger);
    if (!product?.productHandle) return;

    trackEvent('collection.quick_view_opened', product);
  });

  // ── Promo banners ───────────────────────────────────────────────────────
  // snippets/collection-promo-banner.liquid tags each banner with
  // data-promo-id/-name/-media, except in the theme editor, so untagged
  // banners are never tracked.

  const PROMO_SELECTOR = '.collection-promo-banner[data-promo-id]';

  /** @param {Element} banner */
  function promoFromBanner(banner) {
    return {
      promoId: banner.dataset.promoId,
      promoName: banner.dataset.promoName || banner.dataset.promoId,
      mediaType: banner.dataset.promoMedia || 'none',
    };
  }

  /** Which part of the banner a link is: the wrapped media, the CTA button, or a link in the rich text. */
  function promoClickTarget(link) {
    if (link.classList.contains('collection-promo-banner__link')) return 'media';
    if (link.classList.contains('collection-promo-banner__button')) return 'cta';
    return 'text';
  }

  onDocumentEvent('click', (event) => {
    const link = event.target.closest('.collection-promo-banner a[href]');
    if (!link) return;

    const banner = link.closest(PROMO_SELECTOR);
    if (!banner) return;

    trackEvent('collection.promo_banner_clicked', { ...promoFromBanner(banner), target: promoClickTarget(link) });
  });

  // ── Promo banner impressions ────────────────────────────────────────────
  // An impression is a banner at least half visible, counted once per banner
  // per page load — so a banner below the fold, or hidden at this breakpoint
  // (promo--hide-*) or by the schedule script (display:none), never counts.
  // Filtering and load-more replace the grid's markup, so a MutationObserver
  // on #ProductGridContainer picks up the new banner elements; already-seen
  // IDs are skipped so a re-render doesn't count the same banner twice.

  const PROMO_VISIBLE_RATIO = 0.5;
  const _seenPromoIds = new Set();
  let _promoObserver = null;

  function onPromoIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || entry.intersectionRatio < PROMO_VISIBLE_RATIO) return;

      const banner = entry.target;
      if (window.getComputedStyle(banner).display === 'none') return;
      _promoObserver.unobserve(banner);

      const promo = promoFromBanner(banner);
      if (_seenPromoIds.has(promo.promoId)) return;
      _seenPromoIds.add(promo.promoId);

      trackEvent('collection.promo_banner_viewed', promo);
    });
  }

  function observePromoBanners() {
    document.querySelectorAll(PROMO_SELECTOR).forEach((banner) => {
      if (!_seenPromoIds.has(banner.dataset.promoId)) _promoObserver.observe(banner);
    });
  }

  function initPromoImpressions() {
    if (typeof IntersectionObserver === 'undefined') return;

    _promoObserver = new IntersectionObserver(
      (entries) => {
        try {
          onPromoIntersect(entries);
        } catch {
          // tracking must never break the page
        }
      },
      { threshold: PROMO_VISIBLE_RATIO },
    );
    observePromoBanners();

    const grid = document.getElementById('ProductGridContainer');
    if (!grid || typeof MutationObserver === 'undefined') return;

    new MutationObserver(() => {
      try {
        observePromoBanners();
      } catch {
        // tracking must never break the page
      }
    }).observe(grid, { childList: true, subtree: true });
  }

  try {
    initPromoImpressions();
  } catch {
    // tracking must never break the page
  }

  // ── Filter clicks ───────────────────────────────────────────────────────

  onDocumentEvent('change', (event) => {
    const input = event.target.closest('facet-filters-form input[type="checkbox"]');
    if (!input) return;

    const details = input.closest('[data-filter-param]');
    if (!details) return;

    trackEvent('collection.filter_clicked', {
      paramName: details.dataset.filterParam,
      filterLabel: details.dataset.filterLabel,
      value: input.value,
      valueLabel: input.dataset.valueLabel || input.value,
      action: input.checked ? 'add' : 'remove',
    });
  });

  // ── Filter combinations ─────────────────────────────────────────────────
  // facets.js dispatches this once per applied filter change (already
  // debounced there), so one combination event covers a whole batch of
  // simultaneous checkbox changes.

  let _lastCombinationKey = null;

  onDocumentEvent('facets:applied', (event) => {
    const { filters, sortBy } = parseAppliedFilters(event.detail?.searchParams ?? '');
    if (filters.length === 0) {
      // Cleared: re-applying the same combination afterwards is a new action.
      _lastCombinationKey = null;
      return;
    }

    const combinationKey = filters.map((f) => `${f.paramName}=${f.value}`).join('&');
    if (combinationKey === _lastCombinationKey) return;
    _lastCombinationKey = combinationKey;

    trackEvent('collection.filter_combination_applied', { filters, combinationKey, filterCount: filters.length, sortBy });
  });
})();
